import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { Plus, PencilSimple, Trash, Truck, MagnifyingGlass, CaretDown, CaretUp, UploadSimple, X, SlidersHorizontal } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';
import { mfgApi, SUPPLIER_TYPES, SUPPLIER_TYPE_BADGE } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import { BizName } from '@/components/BizName';
import SupplierColumnsDialog from './SupplierColumnsDialog';
import { loadConfig, defaultConfig, LS_KEY as COLS_LS_KEY } from './supplierColumns';

const EMPTY = { name: '', name_en: '', code: '', kana: '', short_name: '', zip: '', invoice_reg_no: '', qualified_invoice: null, closing_day: null, payment_terms: '', contact: '', email: '', phone: '', fax: '', country_code: 'JP', supplier_type: 'vendor', address: '', notes: '', payment_info: null };
const COUNTRY_CODES = [
  { iso: 'JP', label: '日本 (+81)' },
  { iso: 'US', label: '米国・カナダ (+1)' },
  { iso: 'CN', label: '中国 (+86)' },
  { iso: 'KR', label: '韓国 (+82)' },
  { iso: 'TW', label: '台湾 (+886)' },
  { iso: 'HK', label: '香港 (+852)' },
  { iso: 'TH', label: 'タイ (+66)' },
  { iso: 'VN', label: 'ベトナム (+84)' },
  { iso: 'KH', label: 'カンボジア (+855)' },
  { iso: 'ID', label: 'インドネシア (+62)' },
  { iso: 'PH', label: 'フィリピン (+63)' },
  { iso: 'MY', label: 'マレーシア (+60)' },
  { iso: 'SG', label: 'シンガポール (+65)' },
  { iso: 'IN', label: 'インド (+91)' },
  { iso: 'DE', label: 'ドイツ (+49)' },
  { iso: 'GB', label: '英国 (+44)' },
];
const fmtTel = (cc, v) => {
  if (!v) return '—';
  const pn = parsePhoneNumberFromString(String(v));
  return pn ? pn.formatInternational() : v;
};
const toE164 = (val, iso) => {
  if (!val || !String(val).trim()) return '';
  const pn = parsePhoneNumberFromString(String(val), iso);
  return pn ? pn.number : String(val).trim();
};

export default function Suppliers() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [colsOpen, setColsOpen] = useState(false);
  const [config, setConfig] = useState(loadConfig);

  useEffect(() => { try { localStorage.setItem(COLS_LS_KEY, JSON.stringify(config)); } catch { /* ignore */ } }, [config]);

  const toggleCol = (key) => setConfig((cfg) => ({ ...cfg, columns: cfg.columns.map((c) => (c.key === key ? { ...c, visible: !c.visible } : c)) }));
  const moveCol = (key, delta) => setConfig((cfg) => {
    const arr = [...cfg.columns];
    const i = arr.findIndex((c) => c.key === key);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= arr.length) return cfg;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...cfg, columns: arr };
  });
  const setSortCfg = (key, dir) => setConfig((cfg) => ({ ...cfg, sort: { key, dir: dir || 'asc' } }));
  const resetCols = () => setConfig(defaultConfig());

  const load = useCallback(() => { mfgApi.suppliers().then(setList).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setPayOpen(false); setOpen(true); };
  const openEdit = (s) => {
    setEditId(s.id);
    let cc = (s.country_code && /^[A-Z]{2}$/.test(s.country_code)) ? s.country_code : 'JP';
    let phone = s.phone || '';
    let fax = s.fax || '';
    const pp = s.phone ? parsePhoneNumberFromString(String(s.phone)) : null;
    if (pp) { cc = pp.country || cc; phone = pp.formatNational(); }
    const pf = s.fax ? parsePhoneNumberFromString(String(s.fax)) : null;
    if (pf) { fax = pf.formatNational(); if (!pp && pf.country) cc = pf.country; }
    setForm({ ...EMPTY, ...s, country_code: cc, phone, fax });
    setPayOpen(!!(s.payment_info && Object.keys(s.payment_info).length));
    setOpen(true);
  };
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const setPi = (k) => (e) => setForm((p) => ({ ...p, payment_info: { ...(p.payment_info || {}), [k]: e.target.value } }));
  const setTel = (k) => (e) => {
    const cleaned = e.target.value.replace(/[^\d+\s-]/g, '');
    setForm((p) => ({ ...p, [k]: cleaned }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('mfg.supplier.name')); return; }
    setBusy(true);
    try {
      const pi = Object.fromEntries(Object.entries(form.payment_info || {}).filter(([, v]) => String(v || '').trim()));
      const body = { ...form, name: form.name.trim(), payment_info: Object.keys(pi).length ? pi : null,
        phone: toE164(form.phone, form.country_code), fax: toE164(form.fax, form.country_code) };
      if (editId) await mfgApi.updateSupplier(editId, body);
      else await mfgApi.createSupplier(body);
      toast.success(t('mfg.saved')); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };
  const del = async (s) => {
    if (!window.confirm(t('mfg.confirmDelete', { name: s.name }))) return;
    try { await mfgApi.deleteSupplier(s.id); toast.success(t('mfg.deleted')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
  };
  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!window.confirm(t('mfg.supplier.importConfirm'))) return;
    setImporting(true);
    try {
      const res = await mfgApi.importLegacySuppliers(file);
      toast.success(t('mfg.supplier.importDone', { created: res.created, updated: res.updated, skipped: res.skipped }));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || t('mfg.error')); }
    finally { setImporting(false); }
  };

  const filtered = useMemo(
    () => list.filter((s) => !q.trim() || `${s.name} ${s.kana || ''} ${s.short_name || ''} ${s.code || ''} ${s.contact || ''}`.toLowerCase().includes(q.trim().toLowerCase())),
    [list, q],
  );

  // ── 選択(一括削除)ヘルパー ──────────────────────────────────
  const toggleOne = (id) => setSelectedIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const filteredIds = useMemo(() => filtered.map((s) => s.id), [filtered]);
  const allVisibleSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = !allVisibleSelected && filteredIds.some((id) => selectedIds.has(id));
  const toggleAllVisible = () => setSelectedIds((prev) => {
    if (allVisibleSelected) {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.delete(id));
      return next;
    }
    const next = new Set(prev);
    filteredIds.forEach((id) => next.add(id));
    return next;
  });
  const clearSelection = () => setSelectedIds(new Set());

  // 選択解除: リロード後に存在しない ID を除去
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const alive = new Set(list.map((s) => s.id));
      const next = new Set([...prev].filter((id) => alive.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [list]);

  const doBulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const res = await mfgApi.bulkDeleteSuppliers(ids);
      const skippedCount = (res.skipped || []).length;
      if (skippedCount > 0) {
        toast.success(t('mfg.supplier.bulkDeleteDone', { deleted: res.deleted, skipped: skippedCount }));
        // 使用中でスキップされた仕入先名を追加通知(最大3件表示)
        const inUseNames = (res.skipped || [])
          .filter((s) => s.reason === 'in_use')
          .slice(0, 3)
          .map((s) => `${s.name}(${s.in_use_count})`);
        if (inUseNames.length) toast.info(t('mfg.supplier.bulkDeleteSkippedHint', { names: inUseNames.join('、') }));
      } else {
        toast.success(t('mfg.supplier.bulkDeleteAll', { deleted: res.deleted }));
      }
      setBulkConfirmOpen(false);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('mfg.error'));
    } finally {
      setBulkBusy(false);
    }
  };

  // ── DataTable カラム定義: 列設定に応じて表示/非表示 ────────────────
  const makeColumn = (key) => {
    switch (key) {
      case 'name': return {
        key: 'name', primary: true, header: t('mfg.supplier.name'), sortable: true, sortValue: (s) => s.name || '',
        cell: (s) => <BizName ja={s.name} en={s.name_en} className="font-medium text-zinc-800" testid={`supplier-name-${s.id}`} />,
      };
      case 'type': return {
        key: 'type', header: t('mfg.supplier.type'), width: 'w-28', sortable: true, sortValue: (s) => t(`mfg.supplierType.${s.supplier_type || 'vendor'}`),
        cell: (s) => (
          <Badge variant="outline" className={`whitespace-nowrap ${SUPPLIER_TYPE_BADGE[s.supplier_type || 'vendor']}`} data-testid={`supplier-type-${s.id}`}>
            {t(`mfg.supplierType.${s.supplier_type || 'vendor'}`)}
          </Badge>
        ),
      };
      case 'code': return {
        key: 'code', header: t('mfg.supplier.code'), width: 'w-28', sortable: true, sortValue: (s) => s.code || '',
        cell: (s) => <span className="font-mono text-xs text-zinc-600 whitespace-nowrap">{s.code || '—'}</span>,
      };
      case 'contact': return {
        key: 'contact', header: t('mfg.supplier.contact'), sortable: true, sortValue: (s) => s.contact || '',
        cell: (s) => <span className="text-zinc-600 text-xs">{s.contact || '—'}</span>,
      };
      case 'email': return {
        key: 'email', header: t('mfg.supplier.email'), sortable: true, sortValue: (s) => s.email || '',
        cell: (s) => <span className="text-zinc-600 text-xs break-all">{s.email || '—'}</span>,
      };
      case 'phone': return {
        key: 'phone', header: t('mfg.supplier.phone'), width: 'w-40', sortable: true, sortValue: (s) => s.phone || '',
        cell: (s) => <span className="text-zinc-600 whitespace-nowrap font-mono text-xs">{fmtTel(s.country_code, s.phone)}</span>,
      };
      case 'fax': return {
        key: 'fax', header: t('mfg.supplier.fax'), width: 'w-40', sortable: true, sortValue: (s) => s.fax || '',
        cell: (s) => <span className="text-zinc-600 whitespace-nowrap font-mono text-xs">{fmtTel(s.country_code, s.fax)}</span>,
      };
      case 'kana': return {
        key: 'kana', header: t('mfg.supplier.kana'), sortable: true, sortValue: (s) => s.kana || '',
        cell: (s) => <span className="text-zinc-500 text-xs">{s.kana || '—'}</span>,
      };
      case 'short_name': return {
        key: 'short_name', header: t('mfg.supplier.shortName'), sortable: true, sortValue: (s) => s.short_name || '',
        cell: (s) => <span className="text-zinc-600 text-xs">{s.short_name || '—'}</span>,
      };
      case 'zip': return {
        key: 'zip', header: t('mfg.supplier.zip'), width: 'w-28', sortable: true, sortValue: (s) => s.zip || '',
        cell: (s) => <span className="text-zinc-600 text-xs font-mono whitespace-nowrap">{s.zip || '—'}</span>,
      };
      case 'invoice_reg_no': return {
        key: 'invoice_reg_no', header: t('mfg.supplier.invoiceRegNo'), sortable: true, sortValue: (s) => s.invoice_reg_no || '',
        cell: (s) => <span className="text-zinc-600 text-xs font-mono whitespace-nowrap">{s.invoice_reg_no || '—'}</span>,
      };
      case 'address': return {
        key: 'address', header: t('mfg.supplier.address'), sortable: true, sortValue: (s) => s.address || '',
        cell: (s) => <span className="text-zinc-600 text-xs">{s.address || '—'}</span>,
      };
      case 'bank': return {
        key: 'bank', header: t('mfg.supplier.bankName'), sortable: true, sortValue: (s) => (s.payment_info?.bank_name || ''),
        cell: (s) => <span className="text-zinc-600 text-xs">{s.payment_info?.bank_name || '—'}</span>,
      };
      default: return null;
    }
  };

  const selectColumn = canWrite ? {
    key: '_select', width: 'w-10', sortable: false, mobileBadge: true,
    header: (
      <input type="checkbox" data-testid="supplier-select-all"
        checked={allVisibleSelected}
        ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
        onChange={toggleAllVisible}
        className="h-4 w-4 accent-orange-500 cursor-pointer"
        aria-label={t('mfg.supplier.selectAll')} />
    ),
    cell: (s) => (
      <input type="checkbox" data-testid={`supplier-select-${s.id}`}
        checked={selectedIds.has(s.id)}
        onChange={() => toggleOne(s.id)}
        onClick={(e) => e.stopPropagation()}
        className="h-4 w-4 accent-orange-500 cursor-pointer"
        aria-label={t('mfg.supplier.selectRow', { name: s.name })} />
    ),
  } : null;

  const actionsColumn = canWrite ? {
    key: 'actions', actions: true, header: '', width: 'w-24',
    cell: (s) => (<>
      <Button size="sm" variant="ghost" data-testid={`supplier-edit-${s.id}`} onClick={() => openEdit(s)} className="h-9 px-2 text-zinc-600"><PencilSimple size={16} /></Button>
      {canDeleteMfg && <Button size="sm" variant="ghost" data-testid={`supplier-delete-${s.id}`} onClick={() => del(s)} className="h-9 px-2 text-red-600 hover:bg-red-50"><Trash size={16} /></Button>}
    </>),
  } : null;

  const columns = [
    ...(selectColumn ? [selectColumn] : []),
    ...config.columns.filter((c) => c.visible).map((c) => makeColumn(c.key)).filter(Boolean),
    ...(actionsColumn ? [actionsColumn] : []),
  ];

  return (
    <div className="space-y-5" data-testid="mfg-suppliers">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.nav.suppliers')}</h1>
        {canWrite && (
          <div className="flex items-center gap-2">
            <input type="file" accept=".xlsx" id="supplier-import-file" className="hidden" onChange={onImport} data-testid="supplier-import-input" />
            <Button asChild variant="outline" className="h-11 rounded-lg" data-testid="supplier-import-btn">
              <label htmlFor="supplier-import-file" className="cursor-pointer">
                <UploadSimple size={17} className="mr-1.5" /> {importing ? t('mfg.supplier.importing') : t('mfg.supplier.importLegacy')}
              </label>
            </Button>
            <Button data-testid="supplier-new-btn" onClick={openNew} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
              <Plus size={18} className="mr-1.5" weight="bold" /> {t('mfg.new')}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('mfg.search')} className="h-10 pl-9 rounded-lg" />
        </div>
        <Button variant="outline" onClick={() => setColsOpen(true)} data-testid="supplier-columns-btn" className="h-10 rounded-lg">
          <SlidersHorizontal size={16} className="mr-1.5" /> {t('mfg.cols.title')}
        </Button>
      </div>

      <DataTable
        key={`supplier-cols-${config.sort.key || 'none'}-${config.sort.dir}`}
        dataTestId="supplier-table"
        rows={filtered}
        rowKey={(s) => s.id}
        rowTestId={(s) => `supplier-row-${s.id}`}
        defaultSort={config.sort}
        empty={{ icon: <Truck size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.supplier.empty'), testid: 'supplier-empty' }}
        columns={columns}
      />

      <SupplierColumnsDialog open={colsOpen} onClose={() => setColsOpen(false)} config={config}
        onToggle={toggleCol} onMove={moveCol} onSort={setSortCfg} onReset={resetCols} />

      {/* 一括削除アクションバー (フローティング, 選択が1件以上の場合のみ表示) */}
      {canDeleteMfg && selectedIds.size > 0 && (
        <div
          data-testid="supplier-bulk-bar"
          className="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-fit max-w-[95vw] items-center gap-3 rounded-full border border-zinc-200 bg-white/95 backdrop-blur-none px-4 py-2 shadow-lg"
        >
          <span className="text-sm font-medium text-zinc-700" data-testid="supplier-bulk-count">
            {t('mfg.supplier.bulkSelected', { count: selectedIds.size })}
          </span>
          <button
            type="button"
            data-testid="supplier-bulk-clear"
            onClick={clearSelection}
            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-700"
          >
            <X size={12} />
            {t('mfg.supplier.bulkClear')}
          </button>
          <Button
            data-testid="supplier-bulk-delete-btn"
            onClick={() => setBulkConfirmOpen(true)}
            className="h-9 rounded-full bg-red-600 hover:bg-red-700 px-4 text-sm font-medium text-white"
          >
            <Trash size={15} className="mr-1.5" />
            {t('mfg.supplier.bulkDelete')}
          </Button>
        </div>
      )}

      {/* 一括削除 確認ダイアログ */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent className="max-w-md" aria-describedby="supplier-bulk-desc" data-testid="supplier-bulk-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-zinc-900">
              <Trash size={18} className="text-red-600" />
              {t('mfg.supplier.bulkDeleteTitle')}
            </DialogTitle>
            <DialogDescription id="supplier-bulk-desc" className="text-sm text-zinc-600">
              {t('mfg.supplier.bulkDeleteConfirm', { count: selectedIds.size })}
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-zinc-400">{t('mfg.supplier.bulkDeleteHint')}</p>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              data-testid="supplier-bulk-cancel"
              onClick={() => setBulkConfirmOpen(false)}
              disabled={bulkBusy}
            >
              {t('mfg.cancel')}
            </Button>
            <Button
              data-testid="supplier-bulk-confirm"
              onClick={doBulkDelete}
              disabled={bulkBusy}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {bulkBusy ? t('mfg.saving') : t('mfg.supplier.bulkDeleteConfirmBtn')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby={undefined} data-testid="supplier-form-dialog">
          <DialogHeader><DialogTitle>{editId ? t('mfg.edit') : t('mfg.supplier.new')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.name')} *</Label>
              <Input data-testid="supplier-name-input" value={form.name} onChange={set('name')} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.nameEn')}</Label>
              <Input data-testid="supplier-name-en-input" maxLength={80} value={form.name_en || ''} onChange={set('name_en')} placeholder="Sensbey Co., Ltd." className="h-10 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.kana')}</Label><Input data-testid="supplier-kana-input" value={form.kana || ''} onChange={set('kana')} className="h-10 mt-1" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.shortName')}</Label><Input data-testid="supplier-shortname-input" value={form.short_name || ''} onChange={set('short_name')} className="h-10 mt-1" /></div>
            </div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.type')}</Label>
              <Select value={form.supplier_type || 'vendor'} onValueChange={(v) => setForm((p) => ({ ...p, supplier_type: v }))}>
                <SelectTrigger data-testid="supplier-type-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{SUPPLIER_TYPES.map((st) => <SelectItem key={st} value={st}>{t(`mfg.supplierType.${st}`)}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.code')}</Label><Input value={form.code} onChange={set('code')} className="h-10 mt-1" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.contact')}</Label><Input value={form.contact} onChange={set('contact')} className="h-10 mt-1" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.email')}</Label><Input value={form.email} onChange={set('email')} className="h-10 mt-1" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.countryCode')}</Label>
                <Select value={form.country_code} onValueChange={(v) => setForm((p) => ({ ...p, country_code: v }))}>
                  <SelectTrigger data-testid="supplier-country-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRY_CODES.map((c) => <SelectItem key={c.iso} value={c.iso}>{c.label}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.phone')}</Label>
                <Input data-testid="supplier-phone-input" inputMode="numeric" value={form.phone} onChange={setTel('phone')} placeholder="312345678" className="h-10 mt-1 font-mono" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.fax')}</Label>
                <Input data-testid="supplier-fax-input" inputMode="numeric" value={form.fax} onChange={setTel('fax')} placeholder="312345679" className="h-10 mt-1 font-mono" /></div>
            </div>
            <p className="text-[11px] text-zinc-400 -mt-1">{t('mfg.supplier.telHint')}</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.zip')}</Label><Input data-testid="supplier-zip-input" value={form.zip || ''} onChange={set('zip')} placeholder="226-0022" className="h-10 mt-1 font-mono" /></div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.invoiceRegNo')}</Label><Input data-testid="supplier-invoice-input" value={form.invoice_reg_no || ''} onChange={set('invoice_reg_no')} placeholder="T1234567890123" className="h-10 mt-1 font-mono" /></div>
            </div>
            {/* ── 会計 (Accounting — 適格請求書・締め日・支払条件) ── */}
            <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3 space-y-3">
              <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">経理 (Accounting)</p>
              <div className="grid grid-cols-2 gap-3 items-center">
                <label className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 cursor-pointer" data-testid="supplier-qualified-label">
                  <input type="checkbox" data-testid="supplier-qualified-checkbox"
                    checked={!!form.qualified_invoice}
                    onChange={(e) => setForm((p) => ({ ...p, qualified_invoice: e.target.checked }))}
                    className="accent-emerald-500 w-4 h-4" />
                  <span className="text-sm text-zinc-700">適格請求書発行事業者（インボイス対応）</span>
                </label>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">締め日</Label>
                  <Select value={form.closing_day == null ? 'none' : String(form.closing_day)}
                    onValueChange={(v) => setForm((p) => ({ ...p, closing_day: v === 'none' ? null : Number(v) }))}>
                    <SelectTrigger data-testid="supplier-closing-day" className="h-10 mt-1"><SelectValue placeholder="未設定" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">未設定</SelectItem>
                      <SelectItem value="5">5日</SelectItem>
                      <SelectItem value="10">10日</SelectItem>
                      <SelectItem value="15">15日</SelectItem>
                      <SelectItem value="20">20日</SelectItem>
                      <SelectItem value="25">25日</SelectItem>
                      <SelectItem value="99">末日</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs font-semibold text-zinc-600">支払条件</Label>
                <Select value={form.payment_terms || 'none'}
                  onValueChange={(v) => setForm((p) => ({ ...p, payment_terms: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="supplier-payment-terms" className="h-10 mt-1"><SelectValue placeholder="未設定" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">未設定</SelectItem>
                    <SelectItem value="現金">現金</SelectItem>
                    <SelectItem value="翌月10日">翌月10日</SelectItem>
                    <SelectItem value="翌月15日">翌月15日</SelectItem>
                    <SelectItem value="翌月20日">翌月20日</SelectItem>
                    <SelectItem value="翌月末">翌月末</SelectItem>
                    <SelectItem value="翌々月10日">翌々月10日</SelectItem>
                    <SelectItem value="翌々月15日">翌々月15日</SelectItem>
                    <SelectItem value="翌々月末">翌々月末</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.address')}</Label><Input value={form.address} onChange={set('address')} className="h-10 mt-1" /></div>
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.notes')}</Label><Input value={form.notes} onChange={set('notes')} className="h-10 mt-1" /></div>
            <button type="button" data-testid="supplier-pay-toggle" onClick={() => setPayOpen((v) => !v)}
              className="w-full flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 px-3 py-2 transition-colors">
              <span className="text-sm font-semibold text-zinc-700">{t('mfg.supplier.paymentInfo')}</span>
              {payOpen ? <CaretUp size={14} className="text-zinc-500" /> : <CaretDown size={14} className="text-zinc-500" />}
            </button>
            {payOpen && (
            <div className="rounded-lg border border-zinc-100 bg-zinc-50/50 p-3 space-y-3" data-testid="supplier-pay-section">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.bankName')}</Label><Input data-testid="supplier-bank-input" value={form.payment_info?.bank_name || ''} onChange={setPi('bank_name')} className="h-10 mt-1" /></div>
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.branchName')}</Label><Input value={form.payment_info?.branch_name || ''} onChange={setPi('branch_name')} className="h-10 mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.bankCode')}</Label><Input value={form.payment_info?.bank_code || ''} onChange={setPi('bank_code')} className="h-10 mt-1 font-mono" /></div>
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.branchCode')}</Label><Input value={form.payment_info?.branch_code || ''} onChange={setPi('branch_code')} className="h-10 mt-1 font-mono" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.accountType')}</Label><Input value={form.payment_info?.account_type || ''} onChange={setPi('account_type')} placeholder="普通 / 当座" className="h-10 mt-1" /></div>
                <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.accountNo')}</Label><Input value={form.payment_info?.account_no || ''} onChange={setPi('account_no')} className="h-10 mt-1 font-mono" /></div>
              </div>
              <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.supplier.accountHolder')}</Label><Input value={form.payment_info?.account_holder || ''} onChange={setPi('account_holder')} className="h-10 mt-1" /></div>
            </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('mfg.cancel')}</Button>
            <Button data-testid="supplier-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.saving') : t('mfg.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
